import { Box, Button, Flex, Text, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'router/routes';

import { RoadmapCard } from '../RoadmapCard/RoadmapCard';
import { SectionHeader } from '../SectionHeader/SectionHeader';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

interface Msg {
  id: string;
  defaultMessage: string;
}

interface RoadmapStep {
  id: string;
  number: number;
  color: string;
  title: Msg;
  body: Msg;
}

// RTL order: step 1 sits on the right, step 3 on the left (matches the design).
const ROADMAP: RoadmapStep[] = [
  {
    id: 'expansion',
    number: 1,
    // coral gradient with a lighter highlight in the upper-start area (radial layered
    // on top of the coral base so the highlight shows through)
    color:
      'radial-gradient(80% 60.24% at 30.4% 20.48%, rgba(255, 140, 128, 0.35) 0%, rgba(255, 92, 79, 0) 60%), linear-gradient(180deg, #FF5C4F 0%, #B83329 100%)',
    title: { id: 'about.roadmap.expansion.title', defaultMessage: 'Regional expansion.' },
    body: {
      id: 'about.roadmap.expansion.body',
      defaultMessage:
        'We aim to bring Ticket Zone to a wider audience and more experiences beyond Iraq.',
    },
  },
  {
    id: 'platform',
    number: 2,
    color: '#1F083C', // deep indigo
    title: { id: 'about.roadmap.platform.title', defaultMessage: 'A platform for events.' },
    body: {
      id: 'about.roadmap.platform.body',
      defaultMessage: 'We are building tools that make organizing and managing events easier.',
    },
  },
  {
    id: 'experiences',
    number: 3,
    // near-black with a coral glow rising from the lower-start corner (radial layered
    // on top of the dark base so the glow shows through)
    color:
      'radial-gradient(80% 60.24% at 30.4% 80.72%, rgba(255, 92, 79, 0.25) 0%, rgba(0, 0, 0, 0) 60%), linear-gradient(180deg, #1E1E1E 0%, #000000 100%)',
    title: { id: 'about.roadmap.experiences.title', defaultMessage: 'New experiences.' },
    body: {
      id: 'about.roadmap.experiences.body',
      defaultMessage:
        'Continuous work on experiences and events you live more easily and enjoyably.',
    },
  },
];

/**
 * About page Roadmap + CTA section (TZ-391): a centered "رؤيتنا / المستقبل اللي نبنيه"
 * header over three coloured {@link RoadmapCard}s (row on desktop, stacked on mobile),
 * followed by the "عندك فعالية؟" call-to-action band with two pill buttons. RTL.
 */
export const RoadmapCtaSection = () => {
  const navigate = useNavigate();

  // "تواصل معنا" → the organizer (B2B) registration flow.
  const handleContact = () => navigate(ROUTES.b2bRegistration);
  // "تصفح الفعاليات" → the events listing on the home page.
  const handleBrowseEvents = () => navigate(ROUTES.home);

  return (
    <Box as='section' dir='rtl' bg='white' aria-labelledby='about-roadmap-heading'>
      <VStack
        w='100%'
        maxW='container.xl'
        mx='auto'
        px={{ base: '20px', md: '100px' }}
        pt={{ base: '48px', md: '80px' }}
        pb={{ base: '40px', md: '64px' }}
        spacing={{ base: '32px', md: '48px' }}
        align='stretch'
      >
        <SectionHeader
          titleId='about-roadmap-heading'
          align='center'
          eyebrow={<FormattedMessage id='about.roadmap.eyebrow' defaultMessage='Our vision' />}
          eyebrowProps={{ fontWeight: '700', fontSize: { base: '15px', md: '16px' } }}
          title={
            <FormattedMessage
              id='about.roadmap.title'
              defaultMessage='The future we are building, step by step'
            />
          }
          titleProps={{ fontWeight: '900', fontSize: { base: '28px', md: '40px' } }}
        />

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '16px', md: '24px' }}
          align='stretch'
        >
          {ROADMAP.map((step) => (
            <Box key={step.id} flex={{ md: '1' }}>
              <RoadmapCard
                number={step.number}
                color={step.color}
                title={
                  <FormattedMessage id={step.title.id} defaultMessage={step.title.defaultMessage} />
                }
                body={
                  <FormattedMessage id={step.body.id} defaultMessage={step.body.defaultMessage} />
                }
              />
            </Box>
          ))}
        </Flex>
      </VStack>

      {/* CTA band — #FAFAF9 with a soft coral radial glow rising from the bottom-center.
        Set via `sx.background` (literal) so Chakra's gradient-token parser leaves the %
        stops alone. The radial is layered on top of the solid base so the glow shows. */}
      <Box
        sx={{
          background:
            'radial-gradient(60.24% 80% at 50.6% 100%, rgba(255, 92, 79, 0.18) 0%, rgba(255, 255, 255, 0) 70%), linear-gradient(0deg, #FAFAF9, #FAFAF9)',
        }}
      >
        <VStack
          w='100%'
          maxW='container.md'
          mx='auto'
          px={{ base: '20px', md: '40px' }}
          py={{ base: '56px', md: '88px' }}
          spacing={{ base: '16px', md: '20px' }}
          align='center'
          textAlign='center'
        >
          <Flex
            align='center'
            gap='6px'
            px='12px'
            py='5px'
            borderRadius='9999px'
            bg='#FFEDE899'
            border='1px solid'
            borderColor='#FF5C4F33'
          >
            <Box boxSize='6px' borderRadius='9999px' bg='#E8573F' />
            <Text
              as='span'
              m={0}
              color='#E8573F'
              fontFamily={FONT_FAMILY}
              fontWeight='500'
              fontSize='13px'
              lineHeight='140%'
            >
              <FormattedMessage id='about.roadmap.cta.eyebrow' defaultMessage='For organizers' />
            </Text>
          </Flex>

          <Text
            as='h2'
            m={0}
            color='#1B1917'
            fontFamily={FONT_FAMILY}
            fontWeight='900'
            fontSize={{ base: '32px', md: '48px' }}
            lineHeight='115%'
          >
            <FormattedMessage
              id='about.roadmap.cta.title'
              defaultMessage='Do you have <accent>an event?</accent>'
              values={{
                accent: (chunks) => (
                  <Box as='span' color='#E8573F'>
                    {chunks}
                  </Box>
                ),
              }}
            />
          </Text>

          <Text
            as='p'
            m={0}
            maxW='520px'
            color='dark.D500'
            fontFamily={FONT_FAMILY}
            fontWeight='400'
            fontSize={{ base: '15px', md: '16px' }}
            lineHeight='175%'
          >
            <FormattedMessage
              id='about.roadmap.cta.subtitle'
              defaultMessage='Let us bring it to its audience. Get in touch and make Ticket Zone your partner in a successful event.'
            />
          </Text>

          <Flex gap='12px' pt={{ base: '8px', md: '12px' }} wrap='wrap' justify='center'>
            <Button
              variant='unstyled'
              onClick={handleContact}
              display='inline-flex'
              alignItems='center'
              justifyContent='center'
              w='180px'
              h='52px'
              px='32px'
              borderRadius='9999px'
              bg='#FF5C4F'
              color='white'
              fontFamily={FONT_FAMILY}
              fontWeight='600'
              fontSize='16px'
              _hover={{ bg: '#D84A33' }}
              _active={{ bg: '#C8412C' }}
            >
              <FormattedMessage id='about.roadmap.cta.contact' defaultMessage='Contact us' />
            </Button>
            <Button
              variant='unstyled'
              onClick={handleBrowseEvents}
              display='inline-flex'
              alignItems='center'
              justifyContent='center'
              w='180px'
              h='52px'
              px='32px'
              borderRadius='9999px'
              bg='transparent'
              color='#1B1917'
              border='1.5px solid'
              borderColor='#1B1917'
              fontFamily={FONT_FAMILY}
              fontWeight='600'
              fontSize='16px'
              transition='background-color 0.2s ease, color 0.2s ease'
              _hover={{ bg: '#1B1917', color: 'white' }}
              _active={{ bg: '#000000', color: 'white' }}
            >
              <FormattedMessage id='about.roadmap.cta.browse' defaultMessage='Browse events' />
            </Button>
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
};
